import {
  createColumnVisibilityStore,
  type ColumnDefinition,
} from "./createColumnVisibilityStore";

/**
 * The tenant table's columns, in render order.
 *
 * Labels match the table headers exactly — the menu is a list of what is on screen, so a different
 * wording there reads as a different column.
 *
 * Two are locked. `name` is the only thing that says which tenant a row is — it carries both the
 * display name and the id underneath it. `actions` is where Edit / Suspend / Deposit live, so
 * hiding it would take the screen's operations away rather than free up width. STT is not locked:
 * it costs almost nothing and identifies nothing.
 */
export const TENANT_COLUMNS = [
  { id: "index", label: "STT" },
  { id: "name", label: "Tenant Name", locked: true },
  { id: "type", label: "Type" },
  { id: "status", label: "Status" },
  { id: "contact", label: "Contact" },
  { id: "phone", label: "Phone Number" },
  { id: "resources", label: "Resources" },
  { id: "balance", label: "Balance" },
  // Its own column, never folded into Balance: bonus credit buys a trial VM and nothing else, so
  // a tenant showing 100,000 here has no money to spend.
  { id: "bonus", label: "Bonus" },
  // Not hiddenByDefault: the store records which columns are HIDDEN, so a new column appears
  // for every operator — which is right here, since nobody has a reason to go looking in a menu
  // for a column they have never seen.
  { id: "agreements", label: "Commitments" },
  { id: "createdAt", label: "Created At" },
  { id: "actions", label: "Actions", locked: true },
] as const satisfies readonly ColumnDefinition[];

export type TenantColumnId = (typeof TENANT_COLUMNS)[number]["id"];

export const useTenantColumnsStore = createColumnVisibilityStore<TenantColumnId>(
  {
    name: "admin.tenants.columns",
    columns: TENANT_COLUMNS,
  },
);
